import {
  ACCEPTED_FILE_IMPORT_EXT,
  FILE_IMPORT_MAX_SIZE,
  STORE_KEY_ID_LIST,
} from "Constants";
import getIntl from "Helpers/intl";
import { logAppError } from "Helpers/logger";
import { loaderUpdater, pageState } from "State/page";
import type { PageStateType } from "Types/state";
import {
  UPLOADER_VARIANT_DANGER,
  UPLOADER_VARIANT_DEFAULT,
  UPLOADER_VARIANT_UPLOADED,
  UPLOADER_VARIANT_UPLOADING,
} from "mailup-web-components";
import type { CallbackInterface } from "recoil";
import {
  type ImportFileWizardState,
  type TableState,
  type UploaderState,
  WizardSteps,
} from "../model";
import {
  deleteImportFile,
  downloadImportExampleFile,
  downloadImportFile,
  fetchDefaultConfirmationMesssage,
  getConfirmationMessages,
  getDefaultMapping,
  getDynamicFields,
  getImportFiles,
  importFilesPreview,
  saveImportConfiguration,
  uploadImportFile,
  extractCopyPaste,
} from "./services";
import {
  importFileIsDirtyState,
  importFileWizardState,
  importFileWizardStepState,
} from "./state/state";
import {
  importTypeState,
  modalDelesteImpostsState,
  tableState,
  uploaderState,
} from "./state/stateStep1";
import {
  TOAST_VARIANT_ERROR,
  toastShow,
  TOAST_VARIANT_SUCCESS,
} from "mailup-web-components";

import { previewTableState } from "./state/stateStep2";
import {
  defaultMappingState,
  dynamicFieldsOptionState,
} from "./state/stateStep3";
import { addGroup, getGroups } from "./asyncSelectGroups";
import {
  messagesConfirmationState,
  modalNewGroupState,
  recipientGroupsState,
} from "./state/stateStep4";
import {
  copyPasteChannelTypeState,
  copyPasteExtractedState,
  copyPasteIsDirtyTextAreaState,
  copyPasteProcessedState,
  copyPasteTextState,
} from "./state/stateCopyPaste";
import { beFspUploadImportFile } from "Api/beFsp";
import { storeGet } from "mailup-react-helpers";

export const resetPageCallback =
  ({ reset, ...cbi }: CallbackInterface) =>
  async () => {
    reset(pageState);
    reset(importFileWizardState);
    reset(importFileWizardStepState);
    reset(tableState);
    reset(importFileIsDirtyState);
    reset(importTypeState);
    reset(dynamicFieldsOptionState);
    reset(defaultMappingState);
    reset(previewTableState);
    reset(recipientGroupsState);
    reset(modalNewGroupState);
    reset(uploaderState);
    reset(modalDelesteImpostsState);
    reset(messagesConfirmationState);
    reset(copyPasteChannelTypeState);
    reset(copyPasteTextState);
    reset(copyPasteExtractedState);
    reset(copyPasteProcessedState);
    reset(copyPasteIsDirtyTextAreaState);
  };

export const changeWizardStepCallback =
  ({ set }: CallbackInterface) =>
  async (nextStep: WizardSteps) => {
    set(importFileWizardStepState, nextStep);
  };

export const goToStep2Callback =
  ({ set, snapshot }: CallbackInterface) =>
  async () => {
    try {
      set<boolean>(importFileIsDirtyState, true);

      set<PageStateType>(pageState, loaderUpdater(true));
      const importType = await snapshot.getPromise(importTypeState);
      const { fileName, file } = await snapshot.getPromise(uploaderState);
      const { fileSelected, fileSelectedExt } =
        await snapshot.getPromise(tableState);
      const fileObj = {
        name: importType === "file" ? fileName : fileSelected,
        ext: importType === "file" ? file?.fileExt : fileSelectedExt,
      };

      set(importFileWizardState, (prev) => ({
        ...prev,
        FileName: fileObj.name,
        FileExt: fileObj.ext,
      }));

      set(previewTableState, (prev) => ({
        ...prev,
        loading: true,
      }));

      const { Separator, Delimitator, IgnoreDoubleQuotes } =
        await snapshot.getPromise(importFileWizardState);
      const resp = await importFilesPreview(fileObj.name, {
        Separator: Separator,
        Delimiter: Delimitator,
        IgnoreDoubleQuotes: IgnoreDoubleQuotes,
      });
      if (resp.Rows.length > 0) {
        set(importFileWizardStepState, WizardSteps.Preview);

        set(previewTableState, (prev) => ({
          ...prev,
          Rows: resp.Rows,
          ShowXlsWarning: resp.ShowXlsWarning,
        }));
      } else {
        toastShow(
          getIntl().formatMessage({
            id: "Load.FileImport.UploaderErrorRecipient",
          }),
          TOAST_VARIANT_ERROR,
        );
      }
    } catch (error) {
      logAppError({
        Component: "ImportFile",
        Method: "goToStep2Callback",
        //Data: JSON.stringify({ idRecipient }),
        appError: error instanceof Error ? error : new Error(String(error)),
      });

      toastShow(
        getIntl().formatMessage({
          id: "Load.FileImport.UploaderErrorRecipientGeneric",
        }),
        TOAST_VARIANT_ERROR,
      );
    }
    set<PageStateType>(pageState, loaderUpdater(false));
    set(previewTableState, (prev) => ({
      ...prev,
      loading: false,
    }));
  };

export const uploadFileCallback =
  ({ set }: CallbackInterface) =>
  async (file: File[] | null) => {
    let retValue = false;
    try {
      const hasFile = file && file.length > 0;
      const fileObj = hasFile ? file[0] : null;

      if (hasFile && fileObj) {
        const ext = getExtension(fileObj.name);
        if (fileObj.size > FILE_IMPORT_MAX_SIZE) {
          set<UploaderState>(uploaderState, (prev) => ({
            ...prev,
            variant: UPLOADER_VARIANT_DANGER,
            hintDanger: getIntl().formatMessage({
              id: "Load.FileImport.UploaderErrorSize",
            }),
            file: undefined,
          }));
          retValue = false;
        } else if (!ACCEPTED_FILE_IMPORT_EXT.includes(ext)) {
          set<UploaderState>(uploaderState, (prev) => ({
            ...prev,
            variant: UPLOADER_VARIANT_DANGER,
            hintDanger: getIntl().formatMessage({
              id: "Load.FileImport.UploaderErrorExtension",
            }),
            file: undefined,
          }));
          retValue = false;
        } else {
          //
          set<UploaderState>(uploaderState, (prev) => ({
            ...prev,
            variant: UPLOADER_VARIANT_UPLOADING,
            hintDanger: "",
            file: {
              name: fileObj.name,
              size: fileObj.size,
              fileObj: fileObj,
              fileExt: ext,
            },
          }));
          const ret = await uploadImportFile(fileObj);

          set<UploaderState>(uploaderState, (prev) => ({
            ...prev,
            variant: UPLOADER_VARIANT_UPLOADED,
            fileName: ret.FileName,
            filePath: ret.Path,
          }));
          retValue = true;
        }
      } else {
        set<UploaderState>(uploaderState, (prev) => ({
          ...prev,
          variant: UPLOADER_VARIANT_DANGER,
          hintDanger: getIntl().formatMessage({
            id: "Load.Alerts.ErrorGeneric",
          }),
          file: undefined,
        }));
        retValue = false;
      }
    } catch (error) {
      logAppError({
        Component: "ImportFile",
        Method: "downloadImportFileCallback",
        //Data: JSON.stringify({ idRecipient }),
        appError: error,
      });

      set<UploaderState>(uploaderState, (prev) => ({
        ...prev,
        variant: UPLOADER_VARIANT_DANGER,
        hintDanger: getIntl().formatMessage({
          id: "Load.Alerts.ErrorGeneric",
        }),
        file: undefined,
      }));
      retValue = false;
    }
    return retValue;
  };

const getExtension = (fileOrUrl: string | undefined) => {
  if (fileOrUrl) {
    const ext = fileOrUrl.split("?")[0].split(".").pop();
    return ext ? `.${ext.toLowerCase()}` : "";
  } else return "";
};

export const deleteFromUploaderCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async () => {
    const { file } = await snapshot.getPromise(uploaderState);
    try {
      set<UploaderState>(uploaderState, (prev) => ({
        ...prev,
        variant: UPLOADER_VARIANT_UPLOADING,
        hintDanger: "",
        file: undefined,
      }));
      const resp = await deleteImportFile({
        FileNames: [file?.name],
      });
      if (!resp.Result) {
        throw new Error("Failed to delete imports: Result is false");
      }
    } catch (error) {
      logAppError({
        Component: "ImportFile",
        Method: "deleteFromUploaderCallback",
        //Data: JSON.stringify({ idRecipient }),
        appError: error,
      });

      toastShow(
        getIntl().formatMessage({ id: "Load.Alerts.ErrorGeneric" }),
        TOAST_VARIANT_ERROR,
      );
    }
    set<UploaderState>(uploaderState, (prev) => ({
      ...prev,
      variant: UPLOADER_VARIANT_DEFAULT,
    }));
    set<ImportFileWizardState>(importFileWizardState, (prev) => ({
      ...prev,
      FileName: "",
    }));
  };

export const getImportFilesCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async (init: boolean) => {
    try {
      set<PageStateType>(pageState, (prev) => ({ ...prev, status: "ready" }));

      if (init) {
        set<PageStateType>(pageState, loaderUpdater(true));
      }
      const { pageSize, pageNumber, orderBy, orderDescending } =
        await snapshot.getPromise(tableState);

      set<TableState>(tableState, (prev) => ({
        ...prev,
        loading: true,
      }));
      const qsParams = {
        pageSize,
        pageNumber,
        orderBy,
        orderDescending,
      };
      const resp = await getImportFiles(qsParams);
      set<TableState>(tableState, (prev) => ({
        ...prev,
        items: resp.ImportedFiles,
        totalItems: resp.TotalCount,
      }));
    } catch (error) {
      logAppError({
        Component: "ImportFile",
        Method: "getImportFilesCallback",
        appError: error,
      });

      set<PageStateType>(pageState, (prev) => ({
        ...prev,
        status: "error",
        errorMessage: getIntl().formatMessage({
          id: "Load.Alerts.ErrorGeneric",
        }),
      }));
    }
    set<PageStateType>(pageState, loaderUpdater(false));
    set<TableState>(tableState, (prev) => ({
      ...prev,
      loading: false,
    }));
  };

export const downloadImportFileCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async (fileName: string) => {
    try {
      const resp = await downloadImportFile(fileName);
      const link = document.createElement("a");
      link.href = `data:application/octet-stream;base64,${resp.FileContent}`;
      link.download = `${encodeURIComponent(resp.FileName)}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      logAppError({
        Component: "ImportFile",
        Method: "downloadImportFileCallback",
        //Data: JSON.stringify({ idRecipient }),
        appError: error,
      });
      toastShow(
        getIntl().formatMessage({ id: "Load.Alerts.ErrorGeneric" }),
        TOAST_VARIANT_ERROR,
      );
    }
  };

export const downloadImportExampleFileCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async () => {
    try {
      const resp = await downloadImportExampleFile();
      const link = document.createElement("a");
      link.href = `data:application/octet-stream;base64,${resp.FileContent}`;
      link.download = `${encodeURIComponent(resp.FileName)}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      logAppError({
        Component: "ImportFile",
        Method: "downloadImportFileCallback",
        //Data: JSON.stringify({ idRecipient }),
        appError: error,
      });
      toastShow(
        getIntl().formatMessage({ id: "Load.Alerts.ErrorGeneric" }),
        TOAST_VARIANT_ERROR,
      );
    }
  };

export const deleteFromListCallback =
  ({ set, snapshot, reset }: CallbackInterface) =>
  async (fileName: string) => {
    try {
      reset(modalDelesteImpostsState);
      const resp = await deleteImportFile({
        FileNames: [fileName],
      });
      if (resp.Result) {
        const { pageSize, pageNumber, orderBy, orderDescending, fileSelected } =
          await snapshot.getPromise(tableState);

        if (fileSelected === fileName) {
          set<TableState>(tableState, (prev) => ({
            ...prev,
            fileSelected: "",
          }));
        }

        set<TableState>(tableState, (prev) => ({
          ...prev,
          loading: true,
        }));
        const qsParams = {
          pageSize,
          pageNumber,
          orderBy,
          orderDescending,
        };
        let resp = await getImportFiles(qsParams);
        if (resp.ImportedFiles.length === 0 && resp.TotalCount > 0) {
          const qsParams = {
            pageSize: pageSize - 1,
            pageNumber,
            orderBy,
            orderDescending,
          };
          resp = await getImportFiles(qsParams);
        }
        if (resp.TotalCount === 0) {
          set(importTypeState, "file");
        } else {
          set<TableState>(tableState, (prev) => ({
            ...prev,
            items: resp.ImportedFiles,
            totalItems: resp.TotalCount,
          }));
        }

        toastShow(
          getIntl().formatMessage({
            id: "Load.FileImportStatus.DeleteSuccess",
          }),
          TOAST_VARIANT_SUCCESS,
        );
      } else {
        throw new Error("Failed to delete imports: Result is false");
      }
    } catch (error) {
      logAppError({
        Component: "ImportFile",
        Method: "deleteFromUploaderCallback",
        //Data: JSON.stringify({ idRecipient }),
        appError: error,
      });

      toastShow(
        getIntl().formatMessage({ id: "Load.Alerts.ErrorGeneric" }),
        TOAST_VARIANT_ERROR,
      );
    }

    set<TableState>(tableState, (prev) => ({
      ...prev,
      loading: false,
    }));
  };

export const importFilesPreviewCallback =
  ({ set, snapshot, reset }: CallbackInterface) =>
  async () => {
    try {
      set(previewTableState, (prev) => ({
        ...prev,
        loading: true,
      }));
      const { FileName, Separator, Delimitator, IgnoreDoubleQuotes } =
        await snapshot.getPromise(importFileWizardState);

      const resp = await importFilesPreview(FileName, {
        Separator: Separator,
        Delimiter: Delimitator,
        IgnoreDoubleQuotes: IgnoreDoubleQuotes,
      });

      if (resp.Rows.length > 0) {
        set(previewTableState, (prev) => ({
          ...prev,
          Rows: resp.Rows,
          ShowXlsWarning: resp.ShowXlsWarning,
        }));
      } else {
        toastShow(
          getIntl().formatMessage({
            id: "Load.FileImport.UploaderErrorRecipient",
          }),
          TOAST_VARIANT_ERROR,
        );
      }
    } catch (error) {
      logAppError({
        Component: "ImportFile",
        Method: "importFilesPreviewCallback",
        //Data: JSON.stringify({ idRecipient }),
        appError: error,
      });

      toastShow(
        getIntl().formatMessage({
          id: "Load.FileImport.UploaderErrorRecipientGeneric",
        }),
        TOAST_VARIANT_ERROR,
      );
    }

    set(previewTableState, (prev) => ({
      ...prev,
      loading: false,
    }));
  };

export const initStep3Callback =
  ({ set, snapshot, reset }: CallbackInterface) =>
  async () => {
    try {
      set<PageStateType>(pageState, loaderUpdater(true));
      const { Rows } = await snapshot.getPromise(previewTableState);

      const [dynamicFieldsResp, defaultMapping] = await Promise.all([
        getDynamicFields(),
        getDefaultMapping(),
      ]);

      set(defaultMappingState, defaultMapping);
      const dontImport = {
        Id: 0,
        Description: getIntl().formatMessage({
          id: "Load.FileImport.DontImport",
        }),
      };

      const email = {
        Id: 111,
        Description: getIntl().formatMessage({
          id: "Load.FileImport.Email",
        }),
      };

      const prefixAndNumber = {
        Id: 444,
        Description: getIntl().formatMessage({
          id: "Load.FileImport.PrefixAndNumber",
        }),
      };

      const prefix = {
        Id: 222,
        Description: getIntl().formatMessage({
          id: "Load.FileImport.Prefix",
        }),
      };

      const phoneNumber = {
        Id: 333,
        Description: getIntl().formatMessage({
          id: "Load.FileImport.PhoneNumber",
        }),
      };

      const dynamicFields = [
        dontImport,
        email,
        prefixAndNumber,
        prefix,
        phoneNumber,
        ...dynamicFieldsResp,
      ];
      set(
        dynamicFieldsOptionState,
        dynamicFields.map((i: any) => ({
          label: i.Description,
          value: i.Id,
        })),
      );
      set(importFileWizardState, (prev) => ({
        ...prev,
        FieldMapping: Rows[0].map((_r: any, index: number) => ({
          FieldID: defaultMapping.Items.find((f) => f.ColumnIndex === index)
            ? defaultMapping.Items.find((f) => f.ColumnIndex === index)?.FieldID
            : 0,
          ColumnIndex: index,
        })),
      }));
    } catch (error) {
      logAppError({
        Component: "ImportFile",
        Method: "initStep3Callback",
        //Data: JSON.stringify({ idRecipient }),
        appError: error,
      });

      set<PageStateType>(pageState, (prev) => ({
        ...prev,
        status: "error",
        errorMessage: getIntl().formatMessage({
          id: "Load.Alerts.ErrorGeneric",
        }),
      }));
    }

    set<PageStateType>(pageState, loaderUpdater(false));
  };

export const abortAssociationCallback =
  ({ set, snapshot, reset }: CallbackInterface) =>
  async () => {
    set<PageStateType>(pageState, (prev) => ({
      ...prev,
      loading: true,
    }));
    const { Rows } = await snapshot.getPromise(previewTableState);
    const defaultMapping = await snapshot.getPromise(defaultMappingState);
    set(importFileWizardState, (prev) => ({
      ...prev,
      FieldMapping: Rows[0].map((_r: any, index: number) => ({
        FieldID: defaultMapping.Items.find((f) => f.ColumnIndex === index)
          ? defaultMapping.Items.find((f) => f.ColumnIndex === index)?.FieldID
          : 0,
        ColumnIndex: index,
      })),
    }));
    set<PageStateType>(pageState, (prev) => ({
      ...prev,
      loading: false,
    }));
  };

export const addGroupCallback =
  ({ set, reset }: CallbackInterface) =>
  async (groupName: string): Promise<number | undefined> => {
    try {
      const sameNameGroups = await getGroups(groupName, true);

      if (sameNameGroups.length > 0) {
        toastShow(
          getIntl().formatMessage({ id: "Load.NewGroup.AlertExistingGroup" }),
          "warning",
        );
        return 0;
      } else {
        const newGroupId = await addGroup(groupName);

        set(importFileWizardState, (prev) => ({
          ...prev,
          GroupIDs: [newGroupId.Id, ...(prev.GroupIDs || [])],
        }));

        reset(modalNewGroupState);
        return newGroupId.Id;
      }
    } catch (error) {
      console.error("### error", error);

      logAppError({
        Component: "ImportFile",
        Method: "addGroupCallback",
        appError: error,
      });

      toastShow(
        getIntl().formatMessage({ id: "Load.Alerts.ErrorGeneric" }),
        TOAST_VARIANT_ERROR,
      );
    }
  };

export const initStep4Callback =
  ({ set, snapshot }: CallbackInterface) =>
  async () => {
    try {
      set<PageStateType>(pageState, loaderUpdater(true));

      const [msgs, active] = await Promise.all([
        getConfirmationMessages(),
        fetchDefaultConfirmationMesssage(),
      ]);

      const msgItems = msgs.items.map((m) => ({
        label:
          m.value === active
            ? getIntl().formatMessage({
                id: "Load.FileImport.UseDafeultEmail",
              })
            : m.label,
        value: m.value,
      }));

      set(messagesConfirmationState, (prev) => ({
        ...prev,
        options: msgItems,
      }));
    } catch (error) {
      logAppError({
        Component: "ImportFile",
        Method: "initStep4Callback",
        //Data: JSON.stringify({ idRecipient }),
        appError: error instanceof Error ? error : new Error(String(error)),
      });

      set<PageStateType>(pageState, (prev) => ({
        ...prev,
        status: "error",
        errorMessage: getIntl().formatMessage({
          id: "Load.Alerts.ErrorGeneric",
        }),
      }));
    }
    set<PageStateType>(pageState, loaderUpdater(false));
    set(previewTableState, (prev) => ({
      ...prev,
      loading: false,
    }));
  };

export const saveImportConfigurationCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async (type: string | undefined) => {
    let ret = 0;
    try {
      set<boolean>(importFileIsDirtyState, false);

      set<PageStateType>(pageState, loaderUpdater(true));
      const wizardState = await snapshot.getPromise(importFileWizardState);
      const copyPasteChannelType = await snapshot.getPromise(
        copyPasteChannelTypeState,
      );
      const { FileName, FileExt, ...rest } = wizardState;
      const req = {
        ...rest,
        FieldMapping:
          type === "file"
            ? rest.FieldMapping.filter((f) => f.FieldID !== 0)
            : [
                {
                  FieldID: copyPasteChannelType === "Email" ? 111 : 444,
                  ColumnIndex: 0,
                },
              ],
        IgnoreFirstRow: type === "file" ? rest.IgnoreFirstRow : false,
      };
      const resp = await saveImportConfiguration(FileName, req);
      if (!resp || !resp.ImportID) {
        throw new Error("SaveImportConfiguration returned an invalid response");
      }
      ret = resp.ImportID;
    } catch (error) {
      logAppError({
        Component: "ImportFile",
        Method: "saveImportConfigurationCallback",
        //Data: JSON.stringify({ idRecipient }),
        appError: error instanceof Error ? error : new Error(String(error)),
      });

      toastShow(
        getIntl().formatMessage({ id: "Load.Alerts.ErrorGeneric" }),
        TOAST_VARIANT_ERROR,
      );
    }
    set<PageStateType>(pageState, loaderUpdater(false));
    return ret;
  };

export const extractCopyPasteCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async () => {
    try {
      set<PageStateType>(pageState, loaderUpdater(true));
      const text = await snapshot.getPromise(copyPasteTextState);
      const channelType = await snapshot.getPromise(copyPasteChannelTypeState);

      const result = await extractCopyPaste(text, channelType);

      set(copyPasteExtractedState, result);
      set(copyPasteTextState, result.join("\n"));
      set(copyPasteProcessedState, true);
      set(copyPasteIsDirtyTextAreaState, false);

      if (result.length === 0) {
        toastShow(
          channelType === "Email"
            ? getIntl().formatMessage({
                id: "Load.FileImport.CopyPasteMailExtractError",
              })
            : getIntl().formatMessage({
                id: "Load.FileImport.CopyPasteSmsExtractError",
              }),
          "warning",
        );
      } else {
        toastShow(
          channelType === "Email"
            ? getIntl().formatMessage({
                id: "Load.FileImport.CopyPasteMailExtractOk",
              })
            : getIntl().formatMessage({
                id: "Load.FileImport.CopyPasteSmsExtractOk",
              }),
          "success",
        );
      }
    } catch (error) {
      logAppError({
        Component: "ImportFile",
        Method: "extractCopyPasteCallback",
        appError: error instanceof Error ? error : new Error(String(error)),
      });

      toastShow(
        getIntl().formatMessage({ id: "Load.Alerts.ErrorGeneric" }),
        TOAST_VARIANT_ERROR,
      );
    }
    set<PageStateType>(pageState, loaderUpdater(false));
  };

const formatDateTimeNow = (): string => {
  const now = new Date();
  const pad = (n: number, len = 2) => String(n).padStart(len, "0");
  return (
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}${pad(now.getMilliseconds(), 3)}`
  );
};

export const goToCopyPasteStep2Callback =
  ({ set, snapshot }: CallbackInterface) =>
  async () => {
    try {
      set<PageStateType>(pageState, loaderUpdater(true));
      set<boolean>(importFileIsDirtyState, true);

      const channelType = await snapshot.getPromise(copyPasteChannelTypeState);
      const extracted = await snapshot.getPromise(copyPasteExtractedState);

      const prefix =
        channelType === "Email" ? "CopyAndPasteEmail" : "CopyAndPasteSms";
      const fileName = `${prefix}_${formatDateTimeNow()}.txt`;

      const fileContent = btoa(extracted.join("\n"));

      const idList: number = storeGet(STORE_KEY_ID_LIST);
      await beFspUploadImportFile(idList, {
        FileName: fileName,
        FileContent: fileContent,
      });

      set(importFileWizardState, (prev) => ({
        ...prev,
        FileName: fileName,
      }));

      set(importFileWizardStepState, WizardSteps.GroupsAndSettings);
    } catch (error) {
      logAppError({
        Component: "ImportFile",
        Method: "goToCopyPasteStep2Callback",
        appError: error instanceof Error ? error : new Error(String(error)),
      });

      toastShow(
        getIntl().formatMessage({ id: "Load.Alerts.ErrorGeneric" }),
        TOAST_VARIANT_ERROR,
      );
    }
    set<PageStateType>(pageState, loaderUpdater(false));
  };
